/**
 * SegmentNode.tsx - Segment node for conditional branch entry points
 *
 * Segment nodes represent conditional branches in a switch step.
 * Each segment contains filter criteria that determines which contacts
 * flow through that branch. Action steps connect BELOW segments via edges.
 *
 * Features:
 * - "Segment #N" title with colored dot indicator
 * - "Included tags" section with TagExpandIcon for OR filters
 * - "Narrow the list" section with TagNarrowIcon for AND/NOT filters
 * - Default segment variant with "everyone else" messaging
 * - Expand/collapse functionality
 */

import * as Api from '@ViewModels';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { css } from 'aphrodite';
import { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { SegmentNodeData } from '../types/stepNodes';
import { segmentNodeStyles } from './segmentNodeStyles';
import { styleSheet as renameModalStyles } from './segmentRenameModalStyles';
import { BRANCH_COLORS, DEFAULT_BRANCH_COLOR } from '../edges/branchEdgeStyles';
import { useAutomationFlowContext } from '../contexts/AutomationFlowContext';
import { SegmentPrimaryFilter } from './components/SegmentPrimaryFilter';
import { TagExpandIcon } from '../../../components/svgs/icons/TagExpandIcon';
import { TagNarrowIcon } from '../../../components/svgs/icons/TagNarrowIcon';
import { TagListBuilder } from '../../../components/entities/tags/TagListBuilder';
import { Checkbox } from '../../../components-v2/Checkbox';
import { Pill } from '../../../components-v2/Pill/Pill';
import { EditItemPenIcon } from '../../../components/svgs/icons/EditItemPenIcon';
import { useNodeHeightMeasurement } from '../hooks/useNodeHeightMeasurement';
import { SEGMENT_NODE } from '../utils/layoutConstants';
import {
	buildCombinedSegmentFilter,
	buildTagAdditionalCriteria,
	isPolicyBasedSegmentTrigger,
	normalizePrimaryCriteria,
	primaryCriteriaHasValue,
} from '../utils/segmentCriteria';

interface SegmentNodeProps extends NodeProps {
	data: SegmentNodeData;
}

function CollapseIcon() {
	return (
		<svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
			<path d='M12 10L8 6L4 10' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
		</svg>
	);
}

function ExpandIcon() {
	return (
		<svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
			<path d='M4 6L8 10L12 6' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
		</svg>
	);
}

/**
 * Get a summary of the filter criteria for compact view
 */
function getFilterSummary(criteria?: Api.IContactFilterCriteria): string {
	if (!criteria) {
		return 'No filter';
	}
	if (criteria.value) {
		return criteria.value;
	}
	if (criteria.property === Api.ContactFilterCriteriaProperty.Tag) {
		return 'Tag filter';
	}
	if (criteria.property === Api.ContactFilterCriteriaProperty.Policy) {
		return 'Policy filter';
	}
	return 'Filter configured';
}

/**
 * Extract tags from filter criteria by operator type
 */
function extractTagsByOperator(criteria: Api.IContactFilterCriteria[], operator: Api.FilterOperator): string[] {
	const tags: string[] = [];
	for (const c of criteria) {
		if (c.op === operator && c.property === Api.ContactFilterCriteriaProperty.Tag) {
			if (c.value) {
				tags.push(c.value);
			}
			if (c.criteria) {
				for (const nested of c.criteria) {
					if (nested.value) {
						tags.push(nested.value);
					}
				}
			}
		}
	}
	return tags;
}

function areStringArraysEqual(a: string[], b: string[]) {
	if (a.length !== b.length) {
		return false;
	}
	for (let i = 0; i < a.length; i++) {
		if (a[i] !== b[i]) {
			return false;
		}
	}
	return true;
}

export function SegmentNode({ data, selected, id }: SegmentNodeProps) {
	const {
		segmentIndex,
		isDefault,
		primaryCriteria,
		additionalCriteria,
		caseColor,
		segmentName,
		globalSegmentOrdinal,
		validationPathState,
	} = data;

	const {
		isReadOnly,
		updateNodeData,
		onUpdateCaseCriteria,
		onUpdateCaseName,
		automationTemplate,
		impersonationContext,
	} = useAutomationFlowContext();

	// Derive whether this segment should use policy-first filtering from trigger type.
	const trigger = automationTemplate?.draft?.trigger || automationTemplate?.published?.trigger;
	const isPolicyTrigger = isPolicyBasedSegmentTrigger(trigger);

	const [isExpanded, setIsExpanded] = useState(data.isExpanded ?? true);
	const contentRef = useRef<HTMLDivElement>(null);

	// Sync local state when parent updates data.isExpanded (e.g., Ctrl+E bulk toggle).
	useEffect(() => {
		setIsExpanded(data.isExpanded ?? true);
	}, [data.isExpanded]);

	// OR tags (included tags that expand the list).
	// Only read from additionalCriteria — the primary filter tag is handled by SegmentPrimaryFilter.
	const [orTags, setOrTags] = useState<string[]>(() => {
		return extractTagsByOperator(additionalCriteria || [], Api.FilterOperator.Or);
	});

	// AND tags (contacts must also have these tags)
	const [andTags, setAndTags] = useState<string[]>(() => {
		return extractTagsByOperator(additionalCriteria || [], Api.FilterOperator.And);
	});

	// NOT tags (exclude these tags)
	const [notTags, setNotTags] = useState<string[]>(() => {
		return extractTagsByOperator(additionalCriteria || [], Api.FilterOperator.Not);
	});

	// Checkbox states for narrow section
	const [showAndTags, setShowAndTags] = useState(andTags.length > 0);
	const [showNotTags, setShowNotTags] = useState(notTags.length > 0);

	// Keep local tag state synchronized when node data is refreshed from parent/server.
	useEffect(() => {
		const nextOrTags = extractTagsByOperator(additionalCriteria || [], Api.FilterOperator.Or);
		const nextAndTags = extractTagsByOperator(additionalCriteria || [], Api.FilterOperator.And);
		const nextNotTags = extractTagsByOperator(additionalCriteria || [], Api.FilterOperator.Not);

		setOrTags(prev => (areStringArraysEqual(prev, nextOrTags) ? prev : nextOrTags));
		setAndTags(prev => (areStringArraysEqual(prev, nextAndTags) ? prev : nextAndTags));
		setNotTags(prev => (areStringArraysEqual(prev, nextNotTags) ? prev : nextNotTags));
		setShowAndTags(nextAndTags.length > 0);
		setShowNotTags(nextNotTags.length > 0);
	}, [additionalCriteria]);

	const hasPrimaryFilter = primaryCriteriaHasValue(primaryCriteria);
	const canEditAdditionalFilters = !isReadOnly && hasPrimaryFilter;

	const segmentColor = isDefault
		? DEFAULT_BRANCH_COLOR
		: caseColor || BRANCH_COLORS[segmentIndex % BRANCH_COLORS.length];
	const displaySegmentNumber = globalSegmentOrdinal || segmentIndex + 1;
	const segmentTitle = `Segment #${displaySegmentNumber}`;

	// Rename modal state
	const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
	const canEditName = !isReadOnly && !isDefault;

	const handleOpenRenameModal = useCallback(() => {
		if (canEditName) {
			setIsRenameModalOpen(true);
		}
	}, [canEditName]);

	const handleRenameClose = useCallback(
		(newName?: string) => {
			setIsRenameModalOpen(false);
			if (newName !== undefined) {
				const trimmed = newName.trim();
				if (trimmed !== (segmentName || '')) {
					updateNodeData?.(id, { segmentName: trimmed || undefined });
					onUpdateCaseName?.(data.parentSwitchNodeId, data.caseId, trimmed);
				}
			}
		},
		[segmentName, id, updateNodeData, onUpdateCaseName, data.parentSwitchNodeId, data.caseId]
	);

	const handleToggleExpand = useCallback(() => {
		setIsExpanded(prev => {
			const next = !prev;
			updateNodeData?.(id, {
				isExpanded: next,
				estimatedHeight: next ? SEGMENT_NODE.MIN_HEIGHT : SEGMENT_NODE.COLLAPSED_HEIGHT,
			});
			return next;
		});
	}, [id, updateNodeData]);

	useNodeHeightMeasurement(
		contentRef,
		id,
		isExpanded,
		typeof data.estimatedHeight === 'number' ? data.estimatedHeight : null,
		updateNodeData
	);

	// Build criteria from current tag state and update node while preserving the primary filter.
	const buildAndUpdateCriteria = useCallback(
		(newOrTags: string[], newAndTags: string[], newNotTags: string[]) => {
			const nextAdditionalCriteria = buildTagAdditionalCriteria(newOrTags, newAndTags, newNotTags);

			updateNodeData?.(id, {
				additionalCriteria: nextAdditionalCriteria,
			});

			const combinedFilter = buildCombinedSegmentFilter(primaryCriteria, nextAdditionalCriteria);
			onUpdateCaseCriteria?.(data.parentSwitchNodeId, data.caseId, combinedFilter);
		},
		[id, primaryCriteria, updateNodeData, onUpdateCaseCriteria, data.parentSwitchNodeId, data.caseId]
	);

	// Handler for SegmentPrimaryFilter criteria changes (policy or single-tag primary filter)
	const handlePrimaryCriteriaChanged = useCallback(
		(criteria?: Api.IContactFilterCriteria) => {
			const normalizedPrimary = normalizePrimaryCriteria(criteria);
			const shouldClearAdditional = !normalizedPrimary;
			const nextOrTags = shouldClearAdditional ? [] : orTags;
			const nextAndTags = shouldClearAdditional ? [] : andTags;
			const nextNotTags = shouldClearAdditional ? [] : notTags;
			const nextAdditionalCriteria = buildTagAdditionalCriteria(nextOrTags, nextAndTags, nextNotTags);

			if (shouldClearAdditional) {
				setOrTags([]);
				setAndTags([]);
				setNotTags([]);
				setShowAndTags(false);
				setShowNotTags(false);
			}

			updateNodeData?.(id, {
				primaryCriteria: normalizedPrimary || null,
				additionalCriteria: nextAdditionalCriteria,
			});

			const combinedFilter = buildCombinedSegmentFilter(normalizedPrimary, nextAdditionalCriteria);
			onUpdateCaseCriteria?.(data.parentSwitchNodeId, data.caseId, combinedFilter);
		},
		[orTags, andTags, notTags, id, updateNodeData, onUpdateCaseCriteria, data.parentSwitchNodeId, data.caseId]
	);

	// Tag handlers for OR tags (included)
	const handleAddOrTag = useCallback(
		(tag: string) => {
			if (!hasPrimaryFilter) {
				return;
			}
			const newOrTags = [...orTags, tag];
			setOrTags(newOrTags);
			buildAndUpdateCriteria(newOrTags, andTags, notTags);
		},
		[hasPrimaryFilter, orTags, andTags, notTags, buildAndUpdateCriteria]
	);

	const handleRemoveOrTag = useCallback(
		(tag: string) => {
			const newOrTags = orTags.filter(t => t !== tag);
			setOrTags(newOrTags);
			buildAndUpdateCriteria(newOrTags, andTags, notTags);
		},
		[orTags, andTags, notTags, buildAndUpdateCriteria]
	);

	// Tag handlers for AND tags (must also have)
	const handleAddAndTag = useCallback(
		(tag: string) => {
			if (!hasPrimaryFilter) {
				return;
			}
			const newAndTags = [...andTags, tag];
			setAndTags(newAndTags);
			buildAndUpdateCriteria(orTags, newAndTags, notTags);
		},
		[hasPrimaryFilter, orTags, andTags, notTags, buildAndUpdateCriteria]
	);

	const handleRemoveAndTag = useCallback(
		(tag: string) => {
			const newAndTags = andTags.filter(t => t !== tag);
			setAndTags(newAndTags);
			buildAndUpdateCriteria(orTags, newAndTags, notTags);
		},
		[orTags, andTags, notTags, buildAndUpdateCriteria]
	);

	// Tag handlers for NOT tags (exclude)
	const handleAddNotTag = useCallback(
		(tag: string) => {
			if (!hasPrimaryFilter) {
				return;
			}
			const newNotTags = [...notTags, tag];
			setNotTags(newNotTags);
			buildAndUpdateCriteria(orTags, andTags, newNotTags);
		},
		[hasPrimaryFilter, orTags, andTags, notTags, buildAndUpdateCriteria]
	);

	const handleRemoveNotTag = useCallback(
		(tag: string) => {
			const newNotTags = notTags.filter(t => t !== tag);
			setNotTags(newNotTags);
			buildAndUpdateCriteria(orTags, andTags, newNotTags);
		},
		[orTags, andTags, notTags, buildAndUpdateCriteria]
	);

	// Checkbox handlers (v2 Checkbox uses onCheckedChange with boolean | 'indeterminate')
	const handleAndCheckChange = useCallback(
		(checked: boolean | 'indeterminate') => {
			if (!hasPrimaryFilter) {
				return;
			}
			const isChecked = checked === true;
			setShowAndTags(isChecked);
			if (!isChecked) {
				setAndTags([]);
				buildAndUpdateCriteria(orTags, [], notTags);
			}
		},
		[hasPrimaryFilter, orTags, notTags, buildAndUpdateCriteria]
	);

	const handleNotCheckChange = useCallback(
		(checked: boolean | 'indeterminate') => {
			if (!hasPrimaryFilter) {
				return;
			}
			const isChecked = checked === true;
			setShowNotTags(isChecked);
			if (!isChecked) {
				setNotTags([]);
				buildAndUpdateCriteria(orTags, andTags, []);
			}
		},
		[hasPrimaryFilter, orTags, andTags, buildAndUpdateCriteria]
	);

	// Compact view for collapsed state
	if (!isExpanded) {
		return (
			<div
				className={css(
					segmentNodeStyles.compactContainer,
					selected && segmentNodeStyles.containerSelected,
					validationPathState === 'path' && segmentNodeStyles.containerPath,
					validationPathState === 'target' && segmentNodeStyles.containerTarget,
					isDefault && segmentNodeStyles.containerDefault
				)}
				style={{ borderColor: segmentColor }}
				onClick={handleToggleExpand}
			>
				<Handle type='target' position={Position.Top} id='target' className={css(segmentNodeStyles.handle)} />

				<div className={css(segmentNodeStyles.compactContent)}>
					<div className={css(segmentNodeStyles.compactLeft)}>
						<div className={css(segmentNodeStyles.colorDot)} style={{ backgroundColor: segmentColor }} />
						<div>
							<h4
								className={css(segmentNodeStyles.segmentTitle)}
								title={segmentName ? `${segmentTitle}: ${segmentName}` : segmentTitle}
							>
								{segmentTitle}
								{segmentName ? `: ${segmentName}` : ''}
							</h4>
							<p className={css(segmentNodeStyles.compactSummary)}>
								{isDefault ? 'Everyone else' : getFilterSummary(primaryCriteria)}
							</p>
						</div>
					</div>
					<button
						type='button'
						className={css(segmentNodeStyles.actionButton)}
						onClick={e => {
							e.stopPropagation();
							handleToggleExpand();
						}}
						aria-label='Expand segment'
					>
						<ExpandIcon />
					</button>
				</div>

				<Handle type='source' position={Position.Bottom} id='default' className={css(segmentNodeStyles.handle)} />
			</div>
		);
	}

	// Default segment view - shows "everyone else" message
	if (isDefault) {
		return (
			<div
				ref={contentRef}
				className={`${css(
					segmentNodeStyles.container,
					selected && segmentNodeStyles.containerSelected,
					validationPathState === 'path' && segmentNodeStyles.containerPath,
					validationPathState === 'target' && segmentNodeStyles.containerTarget,
					segmentNodeStyles.containerDefault
				)} nopan nodrag`}
				style={{ borderColor: segmentColor }}
				onPointerDown={e => e.stopPropagation()}
			>
				<Handle type='target' position={Position.Top} id='target' className={css(segmentNodeStyles.handle)} />

				{/* Header */}
				<div className={css(segmentNodeStyles.header)}>
					<div className={css(segmentNodeStyles.headerLeft)}>
						<div className={css(segmentNodeStyles.colorDot)} style={{ backgroundColor: segmentColor }} />
						<h4 className={css(segmentNodeStyles.segmentTitle)} title={segmentTitle}>
							{segmentTitle}
						</h4>
					</div>
					<div className={css(segmentNodeStyles.headerActions)}>
						<button
							type='button'
							className={css(segmentNodeStyles.actionButton)}
							onClick={handleToggleExpand}
							aria-label='Collapse segment'
						>
							<CollapseIcon />
						</button>
					</div>
				</div>

				{/* Default segment content */}
				<div className={css(segmentNodeStyles.content)}>
					<p className={css(segmentNodeStyles.defaultDescription)}>Everyone else not matching other segments</p>
				</div>

				<Handle type='source' position={Position.Bottom} id='default' className={css(segmentNodeStyles.handle)} />
			</div>
		);
	}

	// Regular segment view with tag filter UI
	return (
		<div
			ref={contentRef}
			className={`${css(
				segmentNodeStyles.container,
				selected && segmentNodeStyles.containerSelected,
				validationPathState === 'path' && segmentNodeStyles.containerPath,
				validationPathState === 'target' && segmentNodeStyles.containerTarget
			)} nopan nodrag`}
			style={{ borderColor: segmentColor }}
			onPointerDown={e => e.stopPropagation()}
		>
			<Handle type='target' position={Position.Top} id='target' className={css(segmentNodeStyles.handle)} />

			{/* Header */}
			<div className={css(segmentNodeStyles.header)}>
				<div className={css(segmentNodeStyles.headerLeft)}>
					<div className={css(segmentNodeStyles.colorDot)} style={{ backgroundColor: segmentColor }} />
					<h4
						className={css(segmentNodeStyles.segmentTitle)}
						title={segmentName ? `${segmentTitle}: ${segmentName}` : segmentTitle}
					>
						{segmentTitle}:{' '}
						{segmentName || <span className={css(segmentNodeStyles.segmentNamePlaceholder)}>Add name</span>}
					</h4>
					{canEditName && (
						<button
							type='button'
							className={css(segmentNodeStyles.editNameButton)}
							onClick={handleOpenRenameModal}
							title='Rename segment'
						>
							<EditItemPenIcon />
						</button>
					)}
				</div>
				<div className={css(segmentNodeStyles.headerActions)}>
					<button
						type='button'
						className={css(segmentNodeStyles.actionButton)}
						onClick={e => {
							e.stopPropagation();
							handleToggleExpand();
						}}
						aria-label='Collapse segment'
					>
						<CollapseIcon />
					</button>
				</div>
			</div>

			{/* Content */}
			<div className={css(segmentNodeStyles.content)}>
				{/* Primary filter (IF dropdown with policy/tag selector) */}
				<SegmentPrimaryFilter
					criteria={primaryCriteria}
					onCriteriaChanged={handlePrimaryCriteriaChanged}
					isDefault={false}
					isReadOnly={isReadOnly}
					isPolicyRenewalTrigger={isPolicyTrigger}
					impersonationContext={impersonationContext}
				/>

				{/* Description */}
				<p className={css(segmentNodeStyles.description)}>You can combine or filter by tags.</p>
				{!hasPrimaryFilter && !isReadOnly && (
					<p className={css(segmentNodeStyles.validationMessage)}>Choose a primary filter.</p>
				)}

				{/* Included tags section (OR) */}
				<div
					className={css(
						segmentNodeStyles.filterSection,
						!hasPrimaryFilter && !isReadOnly && segmentNodeStyles.filterSectionDisabled
					)}
				>
					<div className={css(segmentNodeStyles.filterSectionHeader)}>
						<TagExpandIcon />
						<div>
							<p className={css(segmentNodeStyles.filterSectionTitle)}>
								Included tags{' '}
								<span className={css(segmentNodeStyles.filterSectionSubtext)}>(this will expand the list)</span>
							</p>
						</div>
					</div>
					{canEditAdditionalFilters ? (
						<div className={css(segmentNodeStyles.tagListContainer)}>
							<TagListBuilder
								tags={orTags}
								separator={Api.FilterOperator.Or}
								onTagAdded={handleAddOrTag}
								onTagRemoved={handleRemoveOrTag}
								inputId={`segment-${id}-or-tags`}
								openOnFocus={true}
								impersonationContext={impersonationContext}
							/>
						</div>
					) : !isReadOnly ? (
						<p className={css(segmentNodeStyles.inlineHint)}>Set a primary filter first.</p>
					) : null}
					{(isReadOnly || !canEditAdditionalFilters) && orTags.length > 0 && (
						<div className={css(segmentNodeStyles.tagChipsContainer)}>
							{orTags.map(tag => (
								<Pill key={tag} variant='tag' color='blue' label={tag} />
							))}
						</div>
					)}
				</div>

				{/* Narrow the list section (AND/NOT) */}
				<div
					className={css(
						segmentNodeStyles.filterSection,
						!hasPrimaryFilter && !isReadOnly && segmentNodeStyles.filterSectionDisabled
					)}
				>
					<div className={css(segmentNodeStyles.filterSectionHeader)}>
						<TagNarrowIcon />
						<div>
							<p className={css(segmentNodeStyles.filterSectionTitle)}>
								<span className={css(segmentNodeStyles.boldText)}>Narrow</span> the list
							</p>
						</div>
					</div>

					<div className={css(segmentNodeStyles.narrowContent)}>
						{/* AND tags - Contacts must also have */}
						<label className={css(segmentNodeStyles.checkboxLabel)}>
							<Checkbox
								checked={showAndTags}
								onCheckedChange={handleAndCheckChange}
								disabled={!hasPrimaryFilter || isReadOnly}
							/>
							<span className={css(segmentNodeStyles.checkboxText)}>Contacts must also have these tags...</span>
						</label>
						{showAndTags && canEditAdditionalFilters && (
							<div className={css(segmentNodeStyles.tagListContainer, segmentNodeStyles.indentedTagList)}>
								<TagListBuilder
									tags={andTags}
									separator={Api.FilterOperator.And}
									onTagAdded={handleAddAndTag}
									onTagRemoved={handleRemoveAndTag}
									inputId={`segment-${id}-and-tags`}
									openOnFocus={true}
									impersonationContext={impersonationContext}
								/>
							</div>
						)}
						{showAndTags && (isReadOnly || !canEditAdditionalFilters) && andTags.length > 0 && (
							<div className={css(segmentNodeStyles.tagChipsContainer, segmentNodeStyles.indentedTagList)}>
								{andTags.map(tag => (
									<Pill key={tag} variant='tag' color='blue' label={tag} />
								))}
							</div>
						)}

						{/* NOT tags - Exclude these tags */}
						<label className={css(segmentNodeStyles.checkboxLabel)}>
							<Checkbox
								checked={showNotTags}
								onCheckedChange={handleNotCheckChange}
								disabled={!hasPrimaryFilter || isReadOnly}
							/>
							<span className={css(segmentNodeStyles.checkboxText)}>Exclude these tags...</span>
						</label>
						{showNotTags && canEditAdditionalFilters && (
							<div className={css(segmentNodeStyles.tagListContainer, segmentNodeStyles.indentedTagList)}>
								<TagListBuilder
									tags={notTags}
									separator={Api.FilterOperator.Not}
									onTagAdded={handleAddNotTag}
									onTagRemoved={handleRemoveNotTag}
									inputId={`segment-${id}-not-tags`}
									openOnFocus={true}
									impersonationContext={impersonationContext}
								/>
							</div>
						)}
						{showNotTags && (isReadOnly || !canEditAdditionalFilters) && notTags.length > 0 && (
							<div className={css(segmentNodeStyles.tagChipsContainer, segmentNodeStyles.indentedTagList)}>
								{notTags.map(tag => (
									<Pill key={tag} variant='tag' color='red' label={tag} />
								))}
							</div>
						)}
					</div>
				</div>
			</div>

			<Handle type='source' position={Position.Bottom} id='default' className={css(segmentNodeStyles.handle)} />

			{/* Rename Segment Modal */}
			{isRenameModalOpen && (
				<RenameSegmentModal
					segmentIndex={displaySegmentNumber - 1}
					initialName={segmentName || ''}
					onClose={handleRenameClose}
				/>
			)}
		</div>
	);
}

/**
 * RenameSegmentModal - Portal-based modal for renaming a segment.
 * Mirrors the legacy TextInputModal with "Rename Segment N" title and Rename button.
 */
interface RenameSegmentModalProps {
	segmentIndex: number;
	initialName: string;
	onClose: (newName?: string) => void;
}

function RenameSegmentModal({ segmentIndex, initialName, onClose }: RenameSegmentModalProps) {
	const [name, setName] = useState(initialName);
	const inputRef = useRef<HTMLInputElement>(null);

	// Focus and select the input on mount
	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.focus();
			inputRef.current.select();
		}
	}, []);

	// Close on ESC
	useEffect(() => {
		const handleEscapeKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onClose();
			}
		};
		window.addEventListener('keydown', handleEscapeKey);
		return () => window.removeEventListener('keydown', handleEscapeKey);
	}, [onClose]);

	const handleRename = useCallback(() => {
		onClose(name);
	}, [name, onClose]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Enter') {
				handleRename();
			}
		},
		[handleRename]
	);

	const handleOverlayClick = useCallback(
		(e: React.MouseEvent) => {
			if (e.target === e.currentTarget) {
				onClose();
			}
		},
		[onClose]
	);

	return createPortal(
		<div className={css(renameModalStyles.overlay)} onClick={handleOverlayClick}>
			<div className={css(renameModalStyles.modal)} role='dialog' aria-modal='true'>
				<h3 className={css(renameModalStyles.title)}>Rename Segment {segmentIndex + 1}</h3>
				<input
					ref={inputRef}
					type='text'
					className={css(renameModalStyles.input)}
					value={name}
					onChange={e => setName(e.target.value)}
					onKeyDown={handleKeyDown}
					placeholder='Please enter a name...'
				/>
				<div className={css(renameModalStyles.footer)}>
					<button type='button' className={css(renameModalStyles.cancelButton)} onClick={() => onClose()}>
						Cancel
					</button>
					<button type='button' className={css(renameModalStyles.renameButton)} onClick={handleRename}>
						Rename
					</button>
				</div>
			</div>
		</div>,
		document.body
	);
}
